// src/pages/DashboardPage.tsx — Dashboard principal POW-02
// Spec: POW-02 §3 — KPIs globales + resumen por periodo + desglose por plan
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Users, DollarSign, TrendingUp, RefreshCw, Zap, FileText, Wallet, UserPlus,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { dashboardApi, clientesApi } from '@/services/powApi'
import type {
  ClienteAdmin, DashboardEvolucionIa, DashboardEvolucionIngresos,
  DashboardModo, DashboardRango, DashboardTopCliente,
} from '@/services/powApi'
import { formatMoney, formatNumber } from '@/utils/format'

interface KpiState {
  total_clientes: number
  clientes_activos: number
  clientes_inactivos: number
  ingresos_total: number
  ingresos_periodo: number
  ingresos_anio: number
  tokens_ia: number
  costo_ia_total: number
  costo_ia_periodo: number
  informes_generados: number
  informes_periodo: number
  nuevos_mes: number
}

interface PlanClientes {
  id_plan: number
  nombre: string
  clientes: number
}

interface PlanInformes {
  id_plan: number
  nombre: string
  informes: number
}

const KPI_EMPTY: KpiState = {
  total_clientes: 0, clientes_activos: 0, clientes_inactivos: 0,
  ingresos_total: 0, ingresos_periodo: 0, ingresos_anio: 0,
  tokens_ia: 0, costo_ia_total: 0, costo_ia_periodo: 0,
  informes_generados: 0, informes_periodo: 0, nuevos_mes: 0,
}

const MESES_FULL = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

/** Paleta por posición de plan (catálogo dinámico Start → Enterprise/Corporate). */
const PLAN_COLORS = [
  'var(--color-miva-blue)',
  'var(--color-miva-green)',
  'var(--color-miva-amber)',
  'var(--color-miva-purple)',
  'var(--color-miva-teal)',
  'var(--color-miva-rose)',
]
const planColor = (ix: number) => PLAN_COLORS[ix % PLAN_COLORS.length]

const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const primerDiaMes = (anio: number, mes: number) => isoDate(new Date(anio, mes - 1, 1))
const ultimoDiaMes = (anio: number, mes: number) => isoDate(new Date(anio, mes, 0))
const fechaCorta = (iso: string) => {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
const fmtTokens = (v: number) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${Math.round(v / 1_000)}k`
  return String(v)
}

interface KpiCardProps {
  title: string
  value: string | number
  icon: React.ElementType
  color: string
  prefix?: string
  suffix?: string
  subtitle?: string
}

function KpiCard({ title, value, icon: Icon, color, prefix = '', suffix = '', subtitle }: KpiCardProps) {
  return (
    <div className="kpi-card fade-in">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
             style={{ background: `${color}20`, border: `1px solid ${color}40` }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
      <p className="text-2xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
        {typeof value === 'number'
          ? (prefix === '$' ? formatMoney(value) : `${prefix}${formatNumber(value)}${suffix}`)
          : `${prefix}${value}${suffix}`}
      </p>
      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{title}</p>
      {subtitle && (
        <p className="text-[11px] mt-1 leading-tight" style={{ color: 'var(--color-text-muted)', opacity: 0.85 }}>
          {subtitle}
        </p>
      )}
    </div>
  )
}

interface SectionHeaderProps {
  tone: string
  title: string
  subtitle?: string
  right?: React.ReactNode
}

/** Encabezado de zona: separa visualmente el alcance de cada bloque de métricas. */
function SectionHeader({ tone, title, subtitle, right }: SectionHeaderProps) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 pb-3"
         style={{ borderBottom: '1px solid var(--color-border)' }}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-block w-1.5 h-8 rounded-full flex-shrink-0" style={{ background: tone }} />
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </h2>
          {subtitle && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{subtitle}</p>
          )}
        </div>
      </div>
      {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
    </div>
  )
}

const ESTADO_BADGE: Record<string, string> = {
  activo: 'badge-green', prueba: 'badge-blue', vencido: 'badge-rose', suspendido: 'badge-amber', inactivo: 'badge-amber',
}

const CUSTOM_TOOLTIP_STYLE = {
  background: 'var(--color-surface-700)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  color: 'var(--color-text-primary)',
  fontSize: '0.8rem',
}

const MODOS: { id: DashboardModo; label: string }[] = [
  { id: 'mes', label: 'Mes' },
  { id: 'anio', label: 'Año' },
  { id: 'rango', label: 'Rango' },
]

export default function DashboardPage() {
  const hoy = new Date()
  const [modo, setModo]         = useState<DashboardModo>('mes')
  const [anioSel, setAnioSel]   = useState(hoy.getFullYear())
  const [mesSel, setMesSel]     = useState(hoy.getMonth() + 1)
  const [fechaDesde, setFechaDesde] = useState(primerDiaMes(hoy.getFullYear(), hoy.getMonth() + 1))
  const [fechaHasta, setFechaHasta] = useState(ultimoDiaMes(hoy.getFullYear(), hoy.getMonth() + 1))

  const [kpi, setKpi]                 = useState<KpiState>(KPI_EMPTY)
  const [planesClientes, setPlanesClientes] = useState<PlanClientes[]>([])
  const [planesInformes, setPlanesInformes] = useState<PlanInformes[]>([])
  const [clientes, setClientes]       = useState<ClienteAdmin[]>([])
  const [rango, setRango]             = useState<DashboardRango | null>(null)
  const [evolucionIngresos, setEvolucionIngresos] = useState<DashboardEvolucionIngresos[]>([])
  const [evolucionIa, setEvolucionIa] = useState<DashboardEvolucionIa[]>([])
  const [topClientes, setTopClientes] = useState<DashboardTopCliente[]>([])
  const [rangoError, setRangoError]   = useState('')
  const [loading, setLoading]         = useState(false)
  const [loadingClientes, setLoadingClientes] = useState(false)

  const anios = useMemo(() => {
    const actual = new Date().getFullYear()
    const base = Array.from({ length: 8 }, (_, i) => actual - i)
    return base.includes(anioSel) ? base : [anioSel, ...base].sort((a, b) => b - a)
  }, [anioSel])

  const periodoLabel = rango?.etiqueta
    ?? (modo === 'mes' ? `${MESES_FULL[mesSel - 1]} ${anioSel}`
      : modo === 'anio' ? `Año ${anioSel}`
      : `${fechaCorta(fechaDesde)} – ${fechaCorta(fechaHasta)}`)
  const unidad = (rango?.grano ?? (modo === 'mes' ? 'dia' : 'mes')) === 'dia' ? 'día' : 'mes'

  const refreshData = useCallback(async () => {
    if (modo === 'rango') {
      if (!fechaDesde || !fechaHasta) {
        setRangoError('Selecciona la fecha desde y la fecha hasta.')
        return
      }
      if (fechaDesde > fechaHasta) {
        setRangoError('La fecha desde no puede ser mayor que la fecha hasta.')
        return
      }
    }
    setRangoError('')
    setLoading(true)
    try {
      const params = modo === 'mes'
        ? { anio: anioSel, mes: mesSel }
        : modo === 'anio'
          ? { anio: anioSel }
          : { fecha_desde: fechaDesde, fecha_hasta: fechaHasta }
      const { data } = await dashboardApi.kpis(params)
      setKpi({
        total_clientes:     data.total_clientes ?? ((data.clientes_activos || 0) + (data.clientes_inactivos || 0)),
        clientes_activos:   data.clientes_activos || 0,
        clientes_inactivos: data.clientes_inactivos || 0,
        ingresos_total:     data.ingresos_total ?? 0,
        ingresos_periodo:   data.ingresos_periodo ?? data.ingresos_mes ?? 0,
        ingresos_anio:      data.ingresos_anio || 0,
        tokens_ia:          data.tokens_ia || 0,
        costo_ia_total:     data.costo_ia_total ?? 0,
        costo_ia_periodo:   data.costo_ia_periodo ?? 0,
        informes_generados: data.informes_generados || 0,
        informes_periodo:   data.informes_periodo ?? 0,
        nuevos_mes:         data.nuevos_mes || 0,
      })
      setPlanesClientes(data.clientes_por_plan ?? [])
      setPlanesInformes(data.informes_por_plan ?? [])
      setRango(data.rango ?? null)
      setEvolucionIngresos(data.evolucion_ingresos ?? [])
      setEvolucionIa(data.evolucion_ia ?? [])
      setTopClientes(data.top_clientes_periodo ?? [])
    } catch {
      // Se conserva el último estado visible ante un error de red.
    } finally {
      setLoading(false)
    }
  }, [modo, anioSel, mesSel, fechaDesde, fechaHasta])

  const loadClientes = async () => {
    setLoadingClientes(true)
    try {
      const { data } = await clientesApi.list({ page: 1, page_size: 5, estado: 'activo' })
      setClientes(data.items || [])
    } catch {
      setClientes([])
    } finally {
      setLoadingClientes(false)
    }
  }

  useEffect(() => {
    void refreshData()
  }, [refreshData])

  useEffect(() => {
    void loadClientes()
  }, [])

  const resetPeriodo = () => {
    const h = new Date()
    setModo('mes')
    setAnioSel(h.getFullYear())
    setMesSel(h.getMonth() + 1)
    setFechaDesde(primerDiaMes(h.getFullYear(), h.getMonth() + 1))
    setFechaHasta(ultimoDiaMes(h.getFullYear(), h.getMonth() + 1))
  }

  const totalSegmentosPlan = planesClientes.reduce((s, p) => s + p.clientes, 0)
  const maxInformesPlan = Math.max(1, ...planesInformes.map(p => p.informes))
  const iaSinDatos = evolucionIa.every(d => d.tokens_total === 0 && d.costo_usd === 0)

  const selectorPeriodo = (
    <>
      <div className="inline-flex items-center rounded-lg p-0.5"
           style={{ background: 'var(--color-surface-800)', border: '1px solid var(--color-border)' }}>
        {MODOS.map(m => (
          <button
            key={m.id}
            type="button"
            onClick={() => setModo(m.id)}
            className="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors"
            style={{
              background: modo === m.id ? 'var(--color-miva-teal)' : 'transparent',
              color: modo === m.id ? '#fff' : 'var(--color-text-secondary)',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {modo === 'mes' && (
        <>
          <label className="sr-only" htmlFor="dashboard-mes">Mes</label>
          <select
            id="dashboard-mes"
            className="form-input"
            style={{ height: '36px', width: '140px' }}
            value={mesSel}
            onChange={(e) => setMesSel(Number(e.target.value))}
          >
            {MESES_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <label className="sr-only" htmlFor="dashboard-anio">Año</label>
          <select
            id="dashboard-anio"
            className="form-input"
            style={{ height: '36px', width: '90px' }}
            value={anioSel}
            onChange={(e) => setAnioSel(Number(e.target.value))}
          >
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </>
      )}

      {modo === 'anio' && (
        <>
          <label className="sr-only" htmlFor="dashboard-anio-solo">Año</label>
          <select
            id="dashboard-anio-solo"
            className="form-input"
            style={{ height: '36px', width: '100px' }}
            value={anioSel}
            onChange={(e) => setAnioSel(Number(e.target.value))}
          >
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </>
      )}

      {modo === 'rango' && (
        <>
          <label className="sr-only" htmlFor="dashboard-desde">Desde</label>
          <input
            id="dashboard-desde"
            type="date"
            className="form-input"
            style={{ height: '36px', width: '150px' }}
            value={fechaDesde}
            max={fechaHasta || undefined}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>–</span>
          <label className="sr-only" htmlFor="dashboard-hasta">Hasta</label>
          <input
            id="dashboard-hasta"
            type="date"
            className="form-input"
            style={{ height: '36px', width: '150px' }}
            value={fechaHasta}
            min={fechaDesde || undefined}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </>
      )}

      <button type="button" onClick={resetPeriodo} className="btn btn-ghost btn-sm" title="Volver al mes actual">
        Mes actual
      </button>
    </>
  )

  return (
    <div className="fade-in space-y-6">
      {/* Header (sin selector: el periodo se elige en su propia zona) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Dashboard</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Resumen operativo del SaaS — datos globales y del periodo seleccionado
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void refreshData(); void loadClientes() }}
          disabled={loading}
          className="btn btn-ghost btn-sm flex items-center gap-2 self-start sm:self-auto"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* ══ ZONA 1 · ACUMULADO HISTÓRICO (no depende del periodo) ══ */}
      <section className="space-y-4">
        <SectionHeader
          tone="var(--color-miva-blue)"
          title="Acumulado histórico"
          subtitle="Métricas globales y estado actual. No cambian al seleccionar un periodo."
        />

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <KpiCard title="Total de clientes" value={kpi.total_clientes}
                   subtitle={`${formatNumber(kpi.clientes_activos)} activos · ${formatNumber(kpi.clientes_inactivos)} inactivos`}
                   icon={Users} color="var(--color-miva-blue)" />
          <KpiCard title="Ingresos hasta la fecha" value={kpi.ingresos_total}
                   subtitle="Histórico USD" icon={DollarSign}
                   color="var(--color-miva-green)" prefix="$" />
          <KpiCard title="Tokens IA consumidos" value={kpi.tokens_ia}
                   subtitle="Total OpenRouter" icon={Zap}
                   color="var(--color-miva-purple)" />
          <KpiCard title="Costo IA (USD)" value={kpi.costo_ia_total}
                   subtitle="Total histórico" icon={Wallet}
                   color="var(--color-miva-amber)" prefix="$" />
          <KpiCard title="Informes generados" value={kpi.informes_generados}
                   subtitle="Periodos procesados (histórico)" icon={FileText}
                   color="var(--color-miva-rose)" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Distribución de clientes por plan */}
          <div className="card xl:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
              <div>
                <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                  Distribución de clientes por plan
                </h2>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                  Clientes activos con al menos un periodo cargado del plan.
                </p>
              </div>
              <span className="badge badge-blue">{formatNumber(kpi.clientes_activos)} activos</span>
            </div>
            {planesClientes.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--color-text-muted)' }}>
                Sin planes configurados.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                <div className="relative" style={{ height: 230 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={planesClientes}
                        dataKey="clientes"
                        nameKey="nombre"
                        innerRadius={58}
                        outerRadius={88}
                        paddingAngle={2}
                        isAnimationActive={false}
                      >
                        {planesClientes.map((p, i) => <Cell key={p.id_plan} fill={planColor(i)} />)}
                      </Pie>
                      <Tooltip
                        contentStyle={CUSTOM_TOOLTIP_STYLE}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        formatter={(v: any, n: any) => [`${formatNumber(v)} clientes`, n]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {formatNumber(totalSegmentosPlan)}
                    </span>
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>clientes</span>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {planesClientes.map((p, i) => (
                    <div key={p.id_plan} className="flex items-center justify-between gap-3 text-xs">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ background: planColor(i) }} />
                        <span className="truncate" style={{ color: 'var(--color-text-secondary)' }} title={p.nombre}>
                          {p.nombre}
                        </span>
                      </span>
                      <span className="font-semibold whitespace-nowrap" style={{ color: 'var(--color-text-primary)' }}>
                        {formatNumber(p.clientes)}
                        <span className="font-normal ml-1" style={{ color: 'var(--color-text-muted)' }}>
                          ({totalSegmentosPlan > 0 ? Math.round((p.clientes / totalSegmentosPlan) * 100) : 0}%)
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[11px] mt-3" style={{ color: 'var(--color-text-muted)' }}>
              Un cliente puede tener periodos en más de un plan; por eso la suma de los segmentos puede superar el total de clientes activos.
            </p>
          </div>

          <div className="card">
            <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Informes generados por plan
            </h2>
            <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
              {formatNumber(kpi.informes_generados)} periodos procesados en total.
            </p>
            {planesInformes.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--color-text-muted)' }}>
                Sin datos.
              </p>
            ) : (
              <div className="space-y-3">
                {planesInformes.map((p, i) => (
                  <div key={p.id_plan}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span style={{ color: 'var(--color-text-secondary)' }}>{p.nombre}</span>
                      <b style={{ color: 'var(--color-text-primary)' }}>{formatNumber(p.informes)}</b>
                    </div>
                    <div className="h-2 rounded-full mt-1" style={{ background: 'var(--color-border)' }}>
                      <div className="h-2 rounded-full transition-all"
                           style={{ width: `${Math.round((p.informes / maxInformesPlan) * 100)}%`, background: planColor(i) }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ ZONA 2 · RESUMEN DEL PERIODO (filtro local de esta sección) ══ */}
      <section className="space-y-4">
        <SectionHeader
          tone="var(--color-miva-teal)"
          title="Resumen del periodo"
          subtitle={`Métricas de ${periodoLabel}. El filtro afecta solo a esta sección.`}
          right={selectorPeriodo}
        />
        {rangoError && (
          <p className="text-xs font-medium" style={{ color: 'var(--color-danger)' }}>{rangoError}</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard title="Ingresos del periodo" value={kpi.ingresos_periodo}
                   subtitle={modo === 'mes'
                     ? `${periodoLabel} · Año: ${formatMoney(kpi.ingresos_anio)}`
                     : periodoLabel}
                   icon={TrendingUp} color="var(--color-miva-teal)" prefix="$" />
          <KpiCard title="Costo IA del periodo" value={kpi.costo_ia_periodo}
                   subtitle={`OpenRouter · ${periodoLabel}`} icon={Wallet}
                   color="var(--color-miva-amber)" prefix="$" />
          <KpiCard title="Informes generados en el periodo" value={kpi.informes_periodo}
                   subtitle={`Procesados · ${periodoLabel}`} icon={FileText}
                   color="var(--color-miva-rose)" />
          <KpiCard title="Nuevos clientes del periodo" value={kpi.nuevos_mes}
                   subtitle={`Registrados · ${periodoLabel}`} icon={UserPlus}
                   color="var(--color-miva-green)" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Ingresos por día/mes */}
          <div className="card xl:col-span-2">
            <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              Ingresos por {unidad} (USD) — {periodoLabel}
            </h2>
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={evolucionIngresos}>
                <defs>
                  <linearGradient id="ingresosGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="var(--color-miva-blue)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-miva-blue)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="etiqueta" interval="preserveStartEnd"
                       tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false}
                       tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={CUSTOM_TOOLTIP_STYLE}
                         // eslint-disable-next-line @typescript-eslint/no-explicit-any
                         formatter={(v: any) => [formatMoney(v ?? 0), 'Ingresos']} />
                <Area type="monotone" dataKey="ingresos_mes" name="Ingresos" stroke="var(--color-miva-blue)"
                      fill="url(#ingresosGrad)" strokeWidth={2} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Top 5 clientes por ingresos del periodo */}
          <div className="card">
            <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Top 5 clientes por ingresos
            </h2>
            <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>{periodoLabel}</p>
            {topClientes.length === 0 ? (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--color-text-muted)' }}>
                Sin ingresos en el periodo.
              </p>
            ) : (
              <div className="space-y-3">
                {topClientes.map((c, i) => (
                  <div key={c.id_cliente}>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="min-w-0 truncate" title={c.nombre}
                            style={{ color: 'var(--color-text-secondary)' }}>
                        <b style={{ color: 'var(--color-text-primary)' }}>{i + 1}.</b>{' '}
                        {c.nombre}
                      </span>
                      <b className="whitespace-nowrap" style={{ color: 'var(--color-text-primary)' }}>
                        {formatMoney(c.ingresos_usd)}
                      </b>
                    </div>
                    <div className="h-1.5 rounded-full mt-1" style={{ background: 'var(--color-border)' }}>
                      <div className="h-1.5 rounded-full"
                           style={{ width: `${Math.min(100, c.participacion_pct)}%`, background: 'var(--color-miva-blue)' }} />
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      {formatNumber(c.participacion_pct, { decimals: 1 })}% del periodo · {formatNumber(c.pagos)} pago(s)
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* Evolución del consumo IA: tokens + costo */}
          <div className="card xl:col-span-2">
            <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Consumo IA por {unidad} — tokens y USD — {periodoLabel}
            </h2>
            <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
              Costo registrado por OpenRouter (real o estimado).
            </p>
            <ResponsiveContainer width="100%" height={230}>
              <ComposedChart data={evolucionIa}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="etiqueta" interval="preserveStartEnd"
                       tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="tokens" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false}
                       tickFormatter={fmtTokens} />
                <YAxis yAxisId="usd" orientation="right" tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                       axisLine={false} tickLine={false} tickFormatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Tooltip
                  contentStyle={CUSTOM_TOOLTIP_STYLE}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formatter={(value: any, name: any) => name === 'Tokens'
                    ? [formatNumber(value ?? 0), 'Tokens']
                    : [formatMoney(value ?? 0), 'Costo USD']}
                />
                <Bar yAxisId="tokens" dataKey="tokens_total" name="Tokens" fill="var(--color-miva-purple)"
                     radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Line yAxisId="usd" type="monotone" dataKey="costo_usd" name="Costo USD"
                      stroke="var(--color-miva-amber)" strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-2 text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'var(--color-miva-purple)' }} />
                Tokens (eje izq.)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-0.5" style={{ background: 'var(--color-miva-amber)' }} />
                Costo USD (eje der.)
              </span>
              {iaSinDatos && <span>· Sin consumo en el periodo</span>}
            </div>
          </div>

          {/* Nuevos clientes por día/mes */}
          <div className="card">
            <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              Nuevos clientes por {unidad}
            </h2>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={evolucionIngresos}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                <XAxis dataKey="etiqueta" interval="preserveStartEnd"
                       tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                       axisLine={false} tickLine={false} />
                <Tooltip contentStyle={CUSTOM_TOOLTIP_STYLE} />
                <Bar dataKey="nuevos" name="Nuevos clientes" fill="var(--color-miva-teal)"
                     radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Clientes recientes (estado actual) */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
            Clientes activos recientes
          </h2>
          <a href="/clientes" className="text-xs hover:underline" style={{ color: 'var(--color-miva-blue)' }}>
            Ver todos →
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="miva-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Correo</th>
                <th className="text-right">Créditos</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {loadingClientes ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Cargando...
                  </td>
                </tr>
              ) : clientes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    No hay clientes activos registrados aún.
                  </td>
                </tr>
              ) : clientes.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium">{c.nombre_comercial}</td>
                  <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{c.correo}</td>
                  <td className="text-right font-mono text-xs">{formatNumber(c.saldo_creditos)}</td>
                  <td>
                    <span className={`badge ${ESTADO_BADGE[c.estado] ?? 'badge-blue'}`}>{c.estado}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
